import React from 'react';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

const SalesMonthlyCard = ({
  mode,
  title,
  chartLoading,
  chartData,
  COLORS,
  DASHBOARD_TYPE,
  formatCurrency,
}) => {
  const isRetail = mode === 'retail';
  const monthlyData = isRetail ? chartData?.salesByStoreMonthly : chartData?.salesByBranchMonthly;
  const lookupData = isRetail ? chartData?.salesByStore : chartData?.salesByBranch;

  return (
    <Card className="flex h-full flex-col border border-slate-200/80 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="border-b border-slate-200 pb-2 text-base font-semibold text-slate-900">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        {chartLoading ? (
          <div className="flex justify-center py-8 text-sm text-slate-500">Loading chart...</div>
        ) : monthlyData && monthlyData.length > 0 ? (
          <ChartContainer config={{}} className={isRetail ? 'h-[330px] w-full' : 'h-[400px] w-full'}>
            <div className={`mx-auto h-full ${isRetail ? 'w-full' : 'w-full'}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={monthlyData}
                  margin={isRetail ? { top: 16, right: 4, left: 0, bottom: 46 } : { top: 20, right: 30, left: 20, bottom: 60 }}
                  barCategoryGap={isRetail ? 0 : '20%'}
                  barGap={0}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: isRetail ? DASHBOARD_TYPE.axisLabel : 12, fill: '#4a5568' }} angle={-45} textAnchor="end" height={80} />
                  <YAxis
                    tick={{ fontSize: isRetail ? DASHBOARD_TYPE.dataLabel : 12, fill: '#4a5568' }}
                    tickFormatter={(value) => {
                      if (value >= 1000000) return `Rp ${(value / 1000000).toFixed(1)}M`;
                      if (value >= 1000) return `Rp ${(value / 1000).toFixed(0)}K`;
                      return `Rp ${value}`;
                    }}
                    width={80}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={(
                      <ChartTooltipContent
                        labelFormatter={(label) => `Month: ${label}`}
                        formatter={(value, name) => (
                          <>
                            <span className="min-w-0 flex-1 truncate text-slate-600">{name}</span>
                            <span className="font-mono font-medium tabular-nums text-slate-950">{formatCurrency(value)}</span>
                          </>
                        )}
                      />
                    )}
                  />
                  {isRetail ? (
                    <Legend
                      verticalAlign="bottom"
                      align="left"
                      content={({ payload = [] }) => (
                        <div className="pl-1 pt-2 md:pl-4 lg:pl-6">
                          <div className="grid grid-cols-2 gap-1 gap-x-2">
                            {payload.map((entry, index) => (
                              <div key={`${entry.value}-${index}`} className="flex min-w-0 items-center gap-1.5 leading-[1.1]">
                                <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ backgroundColor: entry.color }} />
                                <span className="truncate text-[8px] text-slate-600">{entry.value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    />
                  ) : (
                    <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="square" formatter={(value) => <span style={{ fontSize: '12px', color: '#4a5568' }}>{value}</span>} />
                  )}
                  {Object.keys(monthlyData[0]).filter((key) => key !== 'month').map((key, index) => {
                    const found = (lookupData || []).find((item) => item.name === key);
                    return (
                      <Bar key={key} dataKey={key} stackId="a" barSize={isRetail ? 26 : undefined} maxBarSize={isRetail ? 28 : undefined} fill={found?.color || COLORS[index % COLORS.length]} name={key} />
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartContainer>
        ) : (
          <p className="py-8 text-center text-sm text-slate-500">No data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default SalesMonthlyCard;
